import { supabase } from '@/lib/supabase';
import { md5 } from '@/lib/md5';

// ─────────────────────────────────────────────────────────────
// Partage sécurisé d'une note ou d'un jeu de cartes.
//
// Le contenu partagé est figé (copie) dans `core_settings`, sous une clé
// dérivée d'un jeton aléatoire. Le mot de passe est stocké uniquement sous
// forme de condensat MD5 (jamais en clair). Le lien fonctionne sans compte.
// ─────────────────────────────────────────────────────────────

export type SharedKind = 'note' | 'deck';

export interface SharedCard {
  question: string;
  answer: string;
  explanation?: string;
}

export interface SharedItem {
  kind: SharedKind;
  title: string;
  subtitle?: string;
  /** Texte intégral de la note partagée. */
  text?: string;
  /** Cartes du jeu partagé. */
  cards?: SharedCard[];
  passwordHash?: string;
  createdAt: string;
  views?: number;
}

const LEVEL = 'platform';
const SCOPE = 'share';
const keyFor = (token: string) => `link_${token}`;

function newToken(): string {
  const random = Math.random().toString(36).slice(2, 10);
  return `${Date.now().toString(36)}${random}`;
}

export async function createShareLink(
  item: Omit<SharedItem, 'createdAt' | 'passwordHash'>,
  password?: string,
): Promise<{ token: string; url: string }> {
  const token = newToken();
  const payload: SharedItem = {
    ...item,
    passwordHash: password && password.trim() ? md5(password.trim()) : undefined,
    createdAt: new Date().toISOString(),
    views: 0,
  };
  const { error } = await supabase.from('core_settings').insert({
    level: LEVEL,
    scope_ref: SCOPE,
    setting_key: keyFor(token),
    value: payload,
  });
  if (error) throw new Error(error.message || 'Le partage a échoué.');
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const base = typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '/';
  const prefix = base.endsWith('/') ? base.slice(0, -1) : base;
  return { token, url: `${origin}${prefix}/partage-note/${token}` };
}

export async function readShareLink(token: string): Promise<SharedItem | null> {
  if (!token) return null;
  try {
    const { data, error } = await supabase
      .from('core_settings')
      .select('value')
      .eq('level', LEVEL)
      .eq('scope_ref', SCOPE)
      .eq('setting_key', keyFor(token))
      .limit(1);
    if (error) return null;
    const row = (data as { value: SharedItem }[] | null)?.[0];
    if (!row || !row.value) return null;
    return row.value;
  } catch {
    return null;
  }
}

export async function deleteShareLink(token: string): Promise<void> {
  await supabase
    .from('core_settings')
    .delete()
    .eq('level', LEVEL)
    .eq('scope_ref', SCOPE)
    .eq('setting_key', keyFor(token));
}

export function verifySharePassword(item: SharedItem | null, password: string): boolean {
  if (!item) return false;
  if (!item.passwordHash) return true;
  return md5(password.trim()) === item.passwordHash;
}

/** Incrémente le compteur de consultations (best-effort, jamais bloquant). */
export async function touchShareLink(token: string, item: SharedItem): Promise<void> {
  try {
    await supabase
      .from('core_settings')
      .update({ value: { ...item, views: (item.views || 0) + 1 }, updated_at: new Date().toISOString() })
      .eq('level', LEVEL)
      .eq('scope_ref', SCOPE)
      .eq('setting_key', keyFor(token));
  } catch {
    /* compteur facultatif */
  }
}